import { GridLogicOperator } from '@mui/x-data-grid-premium'
import { get, groupBy } from 'lodash'
import { Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { cn } from '~/@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getGridFilterOperators } from '~/common/utils/grid-filters'
import { ActiveUser } from '~/components/settings/types'
import {
  TableColumnAutocomplete,
  TableColumnOption,
} from '~/components/shared/Table/TableColumnAutocomplete'
import {
  DossGridFilterItem,
  DossGridFilterModel,
  shouldClearOperandValueAfterOperatorChange,
} from '~/components/tables/filters'
import { DetailedTable, SimpleTable } from '~/components/tables/types'
import { FilterOperatorSelector } from './FilterOperatorSelector'
import { TableAutocomplete } from './TableAutocomplete'

export type AutocompleteTable = Omit<SimpleTable, 'rootWorkspace'>

interface FilterRowProps {
  index: number
  item: DossGridFilterItem
  columns: TableColumnOption[]
  filterModel: DossGridFilterModel
  onFilterModelChange: (model: DossGridFilterModel) => void
  activeUsers?: ActiveUser[]
  onRemove: (index: number) => void
  showLogicOperator: boolean
  disableFields: boolean
  table: DetailedTable
  showTableDropdown?: boolean
  lookupRefinementMode?: boolean
}

export const canDuplicateFilter = (
  column: TableColumnOption | null | undefined,
  tableId: string,
  lookupRefinementMode: boolean,
): boolean => {
  return column?.table.id === tableId && !lookupRefinementMode
}

export function FilterRow({
  index,
  item,
  columns,
  filterModel,
  onFilterModelChange,
  activeUsers,
  onRemove,
  showLogicOperator,
  disableFields,
  table,
  showTableDropdown = false,
  lookupRefinementMode = false,
}: FilterRowProps) {
  const { t } = useTypedTranslation()

  const tables: AutocompleteTable[] = useMemo(() => {
    const allTables: AutocompleteTable[] = [table]
    // Add child tables to the scope if they are present.
    if (table.childTables.length > 0) {
      allTables.push(
        ...table.childTables.map((childTable) => ({
          ...childTable,
          parentTable: table,
        })),
      )
    }
    return allTables
  }, [table])

  const groupedColumns = useMemo(() => groupBy(columns, (column) => column.table.id), [columns])

  const [selectedTable, setSelectedTable] = useState<AutocompleteTable | null>(
    () => tables.find((t) => t.id === item.tableId) ?? table,
  )
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<TableColumnOption | null>(() => {
    const col = columns.find((col) => col.id === item.field)
    return col ?? { ...table.columns[0], table: { id: table.id, name: table.name } }
  })

  // Sync local state when item changes from outside
  useEffect(() => {
    const newTable = tables.find((t) => t.id === item.tableId) ?? table
    setSelectedTable(newTable)

    const newColumn = columns.find((col) => col.id === item.field)
    if (newColumn) {
      setSelectedFilterColumn(newColumn)
    }
  }, [item.tableId, item.field, tables, table, columns])

  // Get columns for the selected table
  const selectedTableColumns = useMemo(
    () => (selectedTable ? get(groupedColumns, selectedTable.id, []) : []),
    [groupedColumns, selectedTable],
  )

  const column = useMemo(
    () => selectedTableColumns.find((col) => col.id === item.field),
    [selectedTableColumns, item.field],
  )
  const operators = useMemo(() => {
    return column
      ? getGridFilterOperators(column, {
          activeUsers,
          disableFields,
          includeDuplicateOperator: canDuplicateFilter(column, table.id, lookupRefinementMode),
        })
      : []
  }, [activeUsers, column, disableFields, lookupRefinementMode, table.id])

  const operator = useMemo(
    () => operators.find((op) => op.value === item.operator),
    [item.operator, operators],
  )

  const handleTableChange = useCallback(
    (newTable: AutocompleteTable | null) => {
      setSelectedTable(newTable ?? null)
      // If table is cleared (null), don't update the filter - allow typing to search
      if (!newTable) {
        return
      }

      // Get the first column in the new table
      const newTableColumns = get(groupedColumns, newTable.id, [])

      if (newTableColumns.length === 0) {
        return
      }

      const newColumn = newTableColumns[0]
      const newOperators = getGridFilterOperators(newColumn, {
        activeUsers,
        includeDuplicateOperator: canDuplicateFilter(newColumn, table.id, lookupRefinementMode),
      })

      // Update local state to sync with the new column
      setSelectedFilterColumn(newColumn)

      const currentRowIndex = index

      onFilterModelChange({
        ...filterModel,
        items: filterModel.items.map((existingItem, itemIndex) =>
          itemIndex === currentRowIndex
            ? {
                tableId: newColumn.table.id,
                field: newColumn.id,
                operator: newOperators[0]?.value || '',
                value: '',
              }
            : existingItem,
        ),
      })
    },
    [
      activeUsers,
      filterModel,
      groupedColumns,
      index,
      lookupRefinementMode,
      onFilterModelChange,
      table.id,
    ],
  )

  const handleColumnChange = useCallback(
    (newColumn: TableColumnOption | undefined) => {
      setSelectedFilterColumn(newColumn ?? null)
      // When newColumn is undefined, don't update the filter model
      // This allows the user to search for a new column while maintaining the filter item
      if (!newColumn) {
        return
      }

      const newOperators = getGridFilterOperators(newColumn, {
        activeUsers,
        disableFields: true,
        includeDuplicateOperator: canDuplicateFilter(newColumn, table.id, lookupRefinementMode),
      })

      onFilterModelChange({
        ...filterModel,
        items: filterModel.items.map((existingItem, i) =>
          i === index
            ? {
                field: newColumn.id,
                tableId: newColumn.table.id,
                operator: newOperators[0]?.value || '',
                value: '',
              }
            : existingItem,
        ),
      })
    },
    [activeUsers, filterModel, index, lookupRefinementMode, onFilterModelChange, table.id],
  )

  const handleOperatorChange = useCallback(
    (operatorValue: string | null) => {
      if (!operatorValue) {
        return
      }
      const currentOperator = item.operator
      const shouldClearOperandValue = shouldClearOperandValueAfterOperatorChange(
        currentOperator,
        operatorValue,
      )

      const currentIndex = index

      onFilterModelChange({
        ...filterModel,
        items: filterModel.items.map((existingItem, itemIndex) =>
          itemIndex === currentIndex
            ? {
                ...existingItem,
                operator: operatorValue,
                // Only clear the filter value if it is no longer compatible with the new operator
                ...(shouldClearOperandValue && { value: '' }),
              }
            : existingItem,
        ),
      })
    },
    [filterModel, index, item.operator, onFilterModelChange],
  )

  const handleLogicOperatorChange = useCallback(
    (value: GridLogicOperator | undefined) => {
      if (!value) {
        return
      }

      onFilterModelChange({
        ...filterModel,
        logicOperator: value,
      })
    },
    [filterModel, onFilterModelChange],
  )

  const handleApplyValue = useCallback(
    (newItem: DossGridFilterItem) => {
      const currentIndex = index
      onFilterModelChange({
        ...filterModel,
        items: filterModel.items.map((existingItem, itemIndex) =>
          itemIndex === currentIndex
            ? {
                ...existingItem,
                value: newItem.value,
              }
            : existingItem,
        ),
      })
    },
    [index, filterModel, onFilterModelChange],
  )

  const renderFilterInput = useMemo(() => {
    if (!column || !operator || operator.requiresFilterValue === false) {
      return <div className="min-w-72"></div>
    }

    const InputComponent = operator.InputComponent
    if (!InputComponent) {
      return <div className="min-w-72"></div>
    }

    return (
      <div className="min-w-72">
        <InputComponent
          {...operator.InputComponentProps}
          applyValue={handleApplyValue}
          disabled={disableFields}
          item={item}
        />
      </div>
    )
  }, [column, operator, handleApplyValue, disableFields, item])

  const logicOperatorOptions = [
    { label: t('filter.logic_operator_and'), value: GridLogicOperator.And },
    { label: t('filter.logic_operator_or'), value: GridLogicOperator.Or },
  ]

  // Slighly smaller widths when viewing on the bulk lookup selection modal to prevent
  // horizontal scrollbar
  const colWidth = lookupRefinementMode ? 'w-[140px]' : 'w-[200px]'
  const prefixWidth = lookupRefinementMode ? 'min-w-[52px]' : 'min-w-[68px]'
  const operatorWidth = lookupRefinementMode ? 'w-16' : 'w-24'

  return (
    <div className="flex items-center gap-2">
      {index === 0 && (
        <div className={cn(prefixWidth, 'shrink-0 px-2 text-xs')}>{t('filter.row_prefix')}</div>
      )}
      {showLogicOperator && index > 0 && (
        <div className={cn(prefixWidth, 'shrink-0')}>
          <Dropdown
            className={cn(operatorWidth, 'h-9 text-xs [&>*]:px-1')}
            disabled={index !== 1 || disableFields}
            onChange={handleLogicOperatorChange}
            options={logicOperatorOptions}
            value={filterModel.logicOperator}
          />
        </div>
      )}
      {showTableDropdown && (
        <TableAutocomplete
          className={cn(colWidth, 'shrink-0')}
          disabled={disableFields}
          onChange={handleTableChange}
          selectedTable={selectedTable}
          tables={tables}
        />
      )}
      <TableColumnAutocomplete
        className={cn(colWidth, 'shrink-0')}
        columns={selectedTableColumns}
        disabled={disableFields}
        onChange={handleColumnChange}
        selectedColumn={selectedFilterColumn}
        showIcon
        tableId={table.id}
      />

      <FilterOperatorSelector
        activeUsers={activeUsers}
        column={selectedFilterColumn}
        disabled={disableFields}
        includeDuplicateOperator={canDuplicateFilter(
          selectedFilterColumn,
          table.id,
          lookupRefinementMode,
        )}
        onChange={handleOperatorChange}
        value={item.operator}
      />
      {renderFilterInput}
      <InputWrapper>
        <Button
          className="size-9 shrink-0 rounded p-0"
          disabled={disableFields}
          onClick={() => onRemove(index)}
          variant="outline"
        >
          <Trash2 className="size-3" />
        </Button>
      </InputWrapper>
    </div>
  )
}
